import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  message,
  Popconfirm,
  Space,
  Pagination,
  Select,
  Input,
  Tooltip,
  Tag,
  Typography,
  Row,
  Col,
  TableProps,
  theme,
} from 'antd';
import { HomeOutlined, DeleteOutlined, EditOutlined, FilterOutlined } from '@ant-design/icons';
import { apiRequest } from '../../../service/apiRequest';
import { useCurrentModule } from '../../../hooks/useCurrentModule';
import { showSuccess, showError } from '../../../components/errorHandler';
import ExpenseModal from './ExpenseModal';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;

interface Expense {
  expense_id: number;
  expense_title: string;
  expense_amount: number;
  expense_date: string;
  category_name: string;
  expense_category_id: number;
  expense_payment_mode: string;
  expense_remarks: string;
  ledger_name: string;
}

const Expenses: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(false);
  const currentModule = useCurrentModule();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [filters, setFilters] = useState({
    category_id: '',
    search: '',
  });

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchExpenses = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      let url = `/get-expenses?page=${page}&limit=${pageSize}`;
      if (filters.category_id) url += `&category_id=${filters.category_id}`;
      if (filters.search) url += `&search=${filters.search}`;

      const response = await apiRequest({
        method: 'get',
        url,
      });

      if (response) {
        setExpenses(response.data || response); // Handle variations in API response structure
        setPagination({
          current: response.page || page,
          pageSize: response.limit || pageSize,
          total: response.total || (response.length || 0),
        });
      }
    } catch (error) {
      console.error('Fetch Expenses Error:', error);
      showError('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [filters]);

  const handleEditClick = (record: Expense) => {
    setIsEditMode(true);
    setSelectedExpense(record);
    setIsModalVisible(true);
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setSelectedExpense(null);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-expense/${id}`,
      });
      showSuccess('Expense deleted successfully');
      fetchExpenses(pagination.current, pagination.pageSize);
    } catch (error) {
      showError('Failed to delete expense');
    }
  };

  const columns: TableProps<Expense>['columns'] = [
    {
      title: 'Expense Title',
      dataIndex: 'expense_title',
      key: 'expense_title',
      render: (text, record) => (
        <div style={{ lineHeight: '1.4' }}>
          <Text strong>{text}</Text>
          {record.expense_remarks && (
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.expense_remarks}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      width: 150,
      render: (text) => <Tag color="blue">{text || 'Misc'}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'expense_amount',
      key: 'expense_amount',
      width: 150,
      align: 'right',
      render: (amount) => (
        <Text strong style={{ color: '#cf1322' }}>
          ₹{Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'expense_date',
      key: 'expense_date',
      width: 150,
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Method',
      dataIndex: 'expense_payment_mode',
      key: 'expense_payment_mode',
      width: 120,
      render: (mode) => <Tag color="cyan">{mode || 'N/A'}</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          {currentModule?.right_edit === 'Y' && (
            <Tooltip title="Edit Expense">
              <Button
                size="middle"
                type="text"
                style={{ color: '#1890ff', backgroundColor: '#e6f7ff' }}
                icon={<EditOutlined />}
                onClick={() => handleEditClick(record)}
              />
            </Tooltip>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Tooltip title="Delete Expense">
              <Popconfirm
                title="Delete this expense?"
                onConfirm={() => handleDelete(record.expense_id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  size="middle"
                  type="text"
                  danger
                  style={{ backgroundColor: '#fff1f0' }}
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ height: "calc(100vh - 145px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header Section */}
      <div style={{ padding: '0 16px 16px 16px' }}>
        <Row align="middle" justify="space-between" style={{ marginBottom: 12 }}>
          <Col>
            <Breadcrumb style={{ margin: '8px 0' }}>
              <Breadcrumb.Item href="/"><HomeOutlined /></Breadcrumb.Item>
              <Breadcrumb.Item>Expenses</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
          <Col>
            {currentModule?.right_add === 'Y' && (
              <Button type="primary" onClick={showCreateModal}>
                Add Expense
              </Button>
            )}
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Flex gap="small" wrap align="center">
              <Select
                placeholder="Filter by Category"
                allowClear
                style={{ width: 220 }}
                onChange={(value) => setFilters(prev => ({ ...prev, category_id: value }))}
              >
                <Option value={1}>Travel</Option>
                <Option value={2}>Food</Option>
                <Option value={3}>Office</Option>
                <Option value={4}>Misc</Option>
              </Select>

              <Input
                placeholder="Search title or remarks..."
                style={{ width: 250 }}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </Flex>
          </Col>
        </Row>
      </div>

      {/* Table Section */}
      <div
        style={{
          flex: 1,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
          margin: '0 16px 16px 16px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}
      >
        <Table<Expense>
          columns={columns}
          dataSource={expenses}
          rowKey="expense_id"
          loading={loading}
          bordered
          size="middle"
          pagination={false}
          scroll={{ x: 1000, y: 'calc(100vh - 420px)' }}
          style={{ flex: 1 }}
        />

        <div style={{ marginTop: 16 }}>
          <Flex justify="space-between" align="center">
            <Text type="secondary">
              Showing {(pagination.current - 1) * pagination.pageSize + 1}–
              {Math.min(pagination.current * pagination.pageSize, pagination.total)} of {pagination.total}
            </Text>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={(page, pageSize) => fetchExpenses(page, pageSize)}
              showSizeChanger
              size="small"
            />
          </Flex>
        </div>
      </div>

      <ExpenseModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={() => fetchExpenses(pagination.current, pagination.pageSize)}
        isEditMode={isEditMode}
        initialValues={selectedExpense}
      />
    </div>
  );
};

export default Expenses;
